import type { ScoUser } from '@src/graphql-types'

/** Membership SKUs that share one Chargebee subscription and can be prorate-changed. */
export const MEMBERSHIP_LADDER_PRODUCT_IDS = new Set([
  'scom-accelerator-basic',
  'scom-accelerator-tools',
  'scom-accelerator-community',
  'scom-accelerator-advisor',
])

type SubscriptionLike = {
  subscriptionId?: string | null
  isActive?: boolean | null
  cardLabel?: string | null
  productId?: string | null
  product?: {
    productId?: string | null
    termId?: string | null
    effectiveTermId?: string | null
    term?: { termId?: string | null } | null
  } | null
}

const STARTUPS_PRO_PRODUCT_ID = 'scom-accelerator-tools'
const ADVISOR_TEAM_PRODUCT_ID = 'scom-accelerator-advisor'

/**
 * Plan rank on the membership ladder, matching startups3 `productChangeDirection`
 * (`lib/subscriptionChangeRequest.js`). A move to a lower rank is a downgrade: the server
 * refuses it as an immediate change and only schedules it for the end of the term.
 */
export const MEMBERSHIP_PRODUCT_RANK: Readonly<Record<string, number>> = Object.freeze({
  'scom-accelerator-basic': 1,
  'scom-accelerator-tools': 2,
  'scom-accelerator-community': 2,
  'scom-accelerator-advisor': 3,
})

export type MembershipChangeDirection = 'up' | 'down' | 'same'

/**
 * Billing term length in months, matching startups3 `config/productTerms.js`. A shorter term is
 * a downgrade (L57): the server schedules it for the end of the current term, never applies it
 * now, so no credit is created for the unused time.
 */
export const MEMBERSHIP_TERM_MONTHS: Readonly<Record<string, number>> = Object.freeze({
  month: 1,
  threeMonth: 3,
  sixMonth: 6,
  year: 12,
})

/** Direction of a billing-term move. Null when either term is unknown or one-time. */
export function membershipTermDirection(
  fromTermId: string | null | undefined,
  toTermId: string | null | undefined
): MembershipChangeDirection | null {
  const from = fromTermId ? MEMBERSHIP_TERM_MONTHS[fromTermId] : undefined
  const to = toTermId ? MEMBERSHIP_TERM_MONTHS[toTermId] : undefined
  if (from == null || to == null) {
    return null
  }
  if (to === from) {
    return 'same'
  }
  return to > from ? 'up' : 'down'
}

/** Direction of a product move on the ladder. Null when either product is off the ladder. */
export function membershipChangeDirection(
  fromProductId: string | null | undefined,
  toProductId: string | null | undefined
): MembershipChangeDirection | null {
  const from = fromProductId ? MEMBERSHIP_PRODUCT_RANK[fromProductId] : undefined
  const to = toProductId ? MEMBERSHIP_PRODUCT_RANK[toProductId] : undefined
  if (from == null || to == null) {
    return null
  }
  if (to === from) {
    return 'same'
  }
  return to > from ? 'up' : 'down'
}

type UserLike = Pick<ScoUser, 'subscriptions'> | { subscriptions?: SubscriptionLike[] | null }

export function subscriptionProductId(s: SubscriptionLike) {
  return s.productId || s.product?.productId || null
}

/** Term the subscription is billed on. Null when the row does not say. */
export function subscriptionTermId(subscription: SubscriptionLike): string | null {
  return (
    subscription.product?.effectiveTermId ||
    subscription.product?.termId ||
    subscription.product?.term?.termId ||
    null
  )
}

/**
 * Term to compare with the plans on screen, for products the member holds.
 * Omitted when they do not hold it. `null` when they hold it but the term is
 * unknown — callers keep that card as the current plan so we do not sell a
 * second copy. When one subscription is already on `viewedTermId`, that term
 * is the value, so the card for it stays current.
 */
export function heldTermByProductId(
  user: UserLike | null | undefined,
  productIds: readonly string[],
  viewedTermId?: string | null
): Record<string, string | null> {
  const wanted = new Set(productIds.filter(Boolean))
  const byProduct = new Map<string, Array<string | null>>()
  for (const subscription of (user?.subscriptions || []) as SubscriptionLike[]) {
    if (!subscription?.isActive) {
      continue
    }
    const productId = subscriptionProductId(subscription)
    if (!productId || !wanted.has(productId)) {
      continue
    }
    const terms = byProduct.get(productId) || []
    terms.push(subscriptionTermId(subscription))
    byProduct.set(productId, terms)
  }

  const held: Record<string, string | null> = {}
  for (const [productId, terms] of byProduct) {
    if (viewedTermId && terms.includes(viewedTermId)) {
      held[productId] = viewedTermId
      continue
    }
    if (terms.some(term => !term)) {
      held[productId] = null
      continue
    }
    held[productId] = terms.find(term => term && term !== viewedTermId) || terms[0] || null
  }
  return held
}

/**
 * True when this product at this term is already what they pay for. Advisor Team
 * covers Startups Pro at any term. An unknown term counts as a match.
 *
 * `scheduledDowngrade`: the caller is offering a move down at renewal (Team → Pro), so a
 * higher plan does not count as covering the lower one. Holding the product itself still does.
 */
export function membershipAlreadyCovers(
  user: UserLike | null | undefined,
  productId: string | null | undefined,
  termId?: string | null,
  options: { scheduledDowngrade?: boolean } = {}
) {
  if (!productId) {
    return false
  }
  const activeIds = ((user?.subscriptions || []) as SubscriptionLike[])
    .filter(subscription => subscription?.isActive)
    .map(subscriptionProductId)
    .filter((id): id is string => Boolean(id))
  if (
    !options.scheduledDowngrade &&
    productId === STARTUPS_PRO_PRODUCT_ID &&
    activeIds.includes(ADVISOR_TEAM_PRODUCT_ID)
  ) {
    return true
  }
  if (!activeIds.includes(productId)) {
    return false
  }
  const held = heldTermByProductId(user, [productId], termId)[productId]
  if (held == null || !termId) {
    return true
  }
  return held === termId
}

/**
 * Active membership-ladder subscriptions with a card on file: the only subscriptions a prorated
 * product change may target. A change replaces the subscription's items, so offering a
 * non-ladder subscription (Startups Unlimited, investor matching, ...) would silently swap that
 * plan out for the membership.
 */
export function getUpgradeableSubscriptions<T extends SubscriptionLike>(
  user?: { subscriptions?: Array<T | null> | null } | null
): T[] {
  return (user?.subscriptions || []).filter((s): s is T => {
    if (!s?.isActive || !s.cardLabel) {
      return false
    }
    const id = subscriptionProductId(s)
    return Boolean(id && MEMBERSHIP_LADDER_PRODUCT_IDS.has(id))
  })
}

/**
 * True when the user should change an existing membership instead of starting a new subscription.
 * Product moves (Startups Pro → Advisor Team) and a different billing term on the same product.
 */
export function canProrateUpgradeToProduct(
  user: UserLike | null | undefined,
  targetProductId: string | null | undefined,
  targetTermId?: string | null
) {
  if (!targetProductId || !MEMBERSHIP_LADDER_PRODUCT_IDS.has(targetProductId)) {
    return false
  }
  const upgradeable = getUpgradeableSubscriptions(user as { subscriptions?: SubscriptionLike[] })
  if (upgradeable.length === 0) {
    return false
  }
  if (membershipAlreadyCovers(user, targetProductId, targetTermId)) {
    return false
  }
  // A move down is never applied now; it is scheduled for renewal (`membershipDowngradeSubscription`).
  if (membershipDowngradeSubscription(user, targetProductId)) {
    return false
  }
  // Nor is a shorter billing term (L57): `membershipTermDowngradeSubscription`.
  if (membershipTermDowngradeSubscription(user, targetProductId, targetTermId)) {
    return false
  }
  const activeProductIds = ((user?.subscriptions || []) as SubscriptionLike[])
    .filter(subscription => subscription?.isActive)
    .map(subscriptionProductId)
  if (!activeProductIds.includes(targetProductId)) {
    return true
  }
  // Same product, different term: only when that subscription can be changed in place.
  return upgradeable.some(subscription => {
    if (subscriptionProductId(subscription) !== targetProductId) {
      return false
    }
    const held = subscriptionTermId(subscription)
    return Boolean(targetTermId && held && held !== targetTermId)
  })
}

/**
 * Prefer the subscription whose term is actually changing. Otherwise prefer Startups Pro
 * when moving to Advisor Team.
 */
export function preferredUpgradeSubscriptionId(
  user: UserLike | null | undefined,
  targetProductId: string | null | undefined,
  targetTermId?: string | null
) {
  const upgradeable = getUpgradeableSubscriptions(user as { subscriptions?: SubscriptionLike[] })
  if (targetTermId) {
    const termChange = upgradeable.find(subscription => {
      if (subscriptionProductId(subscription) !== targetProductId) {
        return false
      }
      const held = subscriptionTermId(subscription)
      return Boolean(held && held !== targetTermId)
    })
    if (termChange?.subscriptionId) {
      return termChange.subscriptionId
    }
  }
  if (targetProductId === ADVISOR_TEAM_PRODUCT_ID) {
    const tools = upgradeable.find(
      subscription => subscriptionProductId(subscription) === STARTUPS_PRO_PRODUCT_ID
    )
    if (tools?.subscriptionId) {
      return tools.subscriptionId
    }
  }
  return upgradeable[0]?.subscriptionId || null
}

/**
 * The active membership a move to `targetProductId` would schedule at renewal: one ranked
 * above the target (Team when the target is Pro). Null when the member already holds the
 * target, or nothing they hold is higher. Lifetime plans never renew, so they never qualify.
 */
export function membershipDowngradeSubscription<T extends SubscriptionLike & { isLifetime?: boolean | null }>(
  user: { subscriptions?: Array<T | null> | null } | UserLike | null | undefined,
  targetProductId: string | null | undefined
): T | null {
  if (!targetProductId || !MEMBERSHIP_LADDER_PRODUCT_IDS.has(targetProductId)) {
    return null
  }
  const active = ((user?.subscriptions || []) as Array<T | null>).filter((subscription): subscription is T =>
    Boolean(subscription?.isActive)
  )
  if (active.some(subscription => subscriptionProductId(subscription) === targetProductId)) {
    return null
  }
  return (
    active.find(
      subscription =>
        Boolean(subscription.subscriptionId) &&
        !subscription.isLifetime &&
        membershipChangeDirection(subscriptionProductId(subscription), targetProductId) === 'down'
    ) || null
  )
}

/**
 * L57: the active membership a change to `targetProductId` on `targetTermId` would schedule at
 * renewal because the billing term gets shorter (Team yearly → Team monthly). Prefers the
 * subscription on the target product; also matches a lower plan on a longer term (Pro yearly →
 * Team monthly), which the server schedules too. Null when a move down the ladder already
 * covers it (`membershipDowngradeSubscription`), for lifetime plans, and for unknown terms.
 */
export function membershipTermDowngradeSubscription<
  T extends SubscriptionLike & { isLifetime?: boolean | null },
>(
  user: { subscriptions?: Array<T | null> | null } | UserLike | null | undefined,
  targetProductId: string | null | undefined,
  targetTermId: string | null | undefined
): T | null {
  if (!targetProductId || !targetTermId || !MEMBERSHIP_LADDER_PRODUCT_IDS.has(targetProductId)) {
    return null
  }
  const candidates = ((user?.subscriptions || []) as Array<T | null>).filter(
    (subscription): subscription is T =>
      Boolean(subscription?.isActive && subscription.subscriptionId && !subscription.isLifetime) &&
      membershipTermDirection(subscriptionTermId(subscription as T), targetTermId) === 'down'
  )
  return (
    candidates.find(subscription => subscriptionProductId(subscription) === targetProductId) ||
    candidates.find(
      subscription => membershipChangeDirection(subscriptionProductId(subscription), targetProductId) === 'up'
    ) ||
    null
  )
}

/**
 * The billing term a member's plan view or checkout uses when the link names none: the term of
 * the membership they already hold (same months). Pro yearly → Team then prices and changes as
 * Team yearly, an immediate prorated upgrade, never a shorter term scheduled for renewal (L57).
 * Only an explicit shorter `termId` takes the scheduled path.
 *
 * Prefers the subscription on `targetProductId`, otherwise the highest-ranked held plan. Null for
 * guests, a target off the ladder, or when no held term is known (callers fall back to monthly).
 */
export function memberDefaultTermId(
  user: UserLike | null | undefined,
  targetProductId?: string | null
): string | null {
  if (targetProductId && !MEMBERSHIP_LADDER_PRODUCT_IDS.has(targetProductId)) {
    return null
  }
  const held = ((user?.subscriptions || []) as Array<SubscriptionLike | null>)
    .filter((subscription): subscription is SubscriptionLike => {
      if (!subscription?.isActive) {
        return false
      }
      const productId = subscriptionProductId(subscription)
      const termId = subscriptionTermId(subscription)
      return Boolean(
        productId && MEMBERSHIP_LADDER_PRODUCT_IDS.has(productId) && termId && MEMBERSHIP_TERM_MONTHS[termId]
      )
    })
    .sort(
      (left, right) =>
        (MEMBERSHIP_PRODUCT_RANK[subscriptionProductId(right) || ''] || 0) -
        (MEMBERSHIP_PRODUCT_RANK[subscriptionProductId(left) || ''] || 0)
    )
  const match =
    (targetProductId && held.find(subscription => subscriptionProductId(subscription) === targetProductId)) ||
    held[0]
  return match ? subscriptionTermId(match) : null
}
